import { useEffect, useState, useRef } from 'react';
import Cookies from 'js-cookie';
import { applyDelta, enrichResultados } from '../utils/wsDelta';
import { useNavigate, useParams } from 'react-router-dom';
import { bbox, lineDistance, lineString } from '../utils/map';
import { getControlById } from '../utils/time';
import SharePanel from '../components/SharePanel';
import TopHeaderMenu from '../components/TopHeaderMenu';
import MsgNetLoading from '../components/MsgNetLoading';
import ResultadoFavoritosList from '../components/ResultadoFavoritosList';
import CompeticionHeaderMenu from '../components/CompeticionHeaderMenu';
import CONFIG from '../config';

function FavoritosScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const ws = useRef(null);
  const wsReconnect = useRef(null);

  const [evento, setEvento] = useState(null);
  const [competicion, setCompeticion] = useState({ mapa_geojson: null, mapa_visible: null });
  const [sectores, setSectores] = useState([]);
  const [sectoresFiltrados, setSectoresFiltrados] = useState([]);
  const [geoJson, setGeoJson] = useState(null);
  const [turfLine, setTurfLine] = useState(null);
  const [turfBounds, setTurfBounds] = useState(null);
  const [turfDistanceRelation] = useState(1.0);
  const [controles, setControles] = useState([]);
  const [controlMeta, setControlMeta] = useState({ distancia: 0 });
  const [resultados, setResultados] = useState([]);
  const [showSharePanel, setShowSharePanel] = useState(false);

  const competicionId = parseInt(params.competicionId, 10);

  // Fetch init data
  useEffect(() => {
    fetch(`${CONFIG.API_URL}/api/1.0/live/${competicionId}/init/`)
      .then(r => r.json())
      .then(data => {
        setCompeticion(data.competicion);
        setEvento(data.evento);
        setSectores(data.sectores || []);
        const ctrl = data.controles || [];
        setControles(ctrl);
        const meta = ctrl.find(c => c.tipo === 'META');
        setControlMeta(meta || { distancia: 0 });
      })
      .catch(err => console.error('live-init error:', err));
  }, [competicionId]);

  // WebSocket
  useEffect(() => {
    const connect = () => {
      if (ws.current) ws.current.close();
      ws.current = new WebSocket(`${CONFIG.WS_URL}/ws/${competicionId}`);
      ws.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.tipo === 'update' || data.tipo === 'delta') {
          const favoritosCookie = Cookies.get('favoritos-' + competicionId);
          let favoritos = [];
          try { favoritos = favoritosCookie ? JSON.parse(favoritosCookie) : []; } catch (e) {}
          if (!favoritos.length) return;
          const favDorsals = favoritos.map(d => parseInt(d, 10));
          const ctrl = data.controles || [];
          if (data.tipo === 'update') {
            const flat = (data.resultados || []).map(r => ({ ...r, ...r.controles }));
            setResultados(enrichResultados(flat.filter(r => favDorsals.includes(r.dorsal)), ctrl));
          } else {
            setResultados(prev => {
              const updated = applyDelta(prev, data.resultados || [], data.eliminados || []);
              return enrichResultados(updated.filter(r => favDorsals.includes(r.dorsal)), ctrl);
            });
          }
          setControles(ctrl);
        }
      };
      ws.current.onclose = () => {
        wsReconnect.current = setTimeout(connect, 3000);
      };
    };
    connect();
    return () => {
      if (wsReconnect.current) clearTimeout(wsReconnect.current);
      if (ws.current) ws.current.close();
    };
  }, [competicionId]);

  // Calcular sectores con mapa
  useEffect(() => {
    if (competicion && controlMeta && controles.length && sectores.length) {
      const sectoresConMapa = sectores.filter(s => s.mapa_geojson);
      if (sectoresConMapa.length) {
        const _turfLine = [];
        let _totalLine = [];
        const _sectores = sectoresConMapa.map(s => {
          let geo = JSON.parse(s.mapa_geojson);
          geo = geo.features[0].geometry.coordinates.map(r => [r[1], r[0]]);
          const line = lineString(geo);
          _turfLine.push(line);
          s.distanciaGeo = lineDistance(line) * 1000.0;
          _totalLine = _totalLine.concat(geo);
          s.inicio_metros = 0.0;
          if (s.inicio_id) {
            const inicio = getControlById(controles, s.inicio_id);
            if (inicio && typeof inicio.distancia === 'number') s.inicio_metros = inicio.distancia;
          }
          const fin = getControlById(controles, s.fin_id);
          s.fin_metros = fin && typeof fin.distancia === 'number' ? fin.distancia : s.inicio_metros;
          s.distancia = s.fin_metros - s.inicio_metros;
          s.turfDistanceRelation = (s.distanciaGeo > 0 && s.distancia > 0) ? s.distancia / s.distanciaGeo : 1.0;
          if (!isFinite(s.turfDistanceRelation)) s.turfDistanceRelation = 1.0;
          return s;
        });
        if (_totalLine.length > 0) setTurfBounds(bbox(lineString(_totalLine)));
        setTurfLine(_turfLine);
        setGeoJson(_sectores.map(s => JSON.parse(s.mapa_geojson).features[0].geometry.coordinates.map(r => [r[1], r[0]])));
        setSectoresFiltrados(_sectores);
      }
    }
  }, [competicion, controlMeta, controles, sectores]);

  if (!competicion || competicion.visible === undefined) return <MsgNetLoading />;

  return (
    <div>
      <TopHeaderMenu
        onClickBack={() => navigate(`/${competicion.evento_id}`)}
        onClickShare={() => setShowSharePanel(!showSharePanel)}
        competicion={competicion}
        evento={evento}
      />
      <CompeticionHeaderMenu competicion={competicion} selected={3} evento={evento} />
      <div className="pt-28">
        <ResultadoFavoritosList
          resultados={resultados}
          competicion={competicion}
          controles={controles}
          sectores={sectoresFiltrados}
          geoJson={geoJson}
          turfLine={turfLine}
          turfBounds={turfBounds}
          turfDistanceRelation={turfDistanceRelation}
        />
        {showSharePanel && (
          <SharePanel
            close={() => setShowSharePanel(false)}
            url={`/competicion/${competicion.id}/favoritos`}
            title={`Progreso de tus favoritos en vivo en la prueba ${competicion.nombre} en ${evento.nombre}`}
          />
        )}
      </div>
    </div>
  );
}

export default FavoritosScreen;
